import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  TextField,
} from '@mui/material';
import {
  Reorder,
  Delete,
  VisibilityOffOutlined,
  VisibilityOutlined,
  Edit,
  Done,
} from '@mui/icons-material';

import { MAX_HABIT_NAME_LENGTH } from '../../constants';
import { getIconComponent } from '../../habitIcons';
import HabitIconPicker from '../HabitIconPicker';
import {
  StyledDraggable,
  StyledListItemText,
  StyledIconButton,
} from './styles';

const HabitItem = ({
  index,
  habitText,
  handleDelete,
  handleEdit,
  handleHide,
  isHidden,
  iconId,
  onIconChange,
  usedIconIds,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState(habitText);
  const [pickerAnchorEl, setPickerAnchorEl] = useState(null);
  const iconButtonRef = useRef(null);

  const handleEditClick = useCallback(() => {
    if (isEditing) {
      setIsEditing(false);
      handleEdit(index, editedText, iconId);
    } else {
      setIsEditing(true);
    }
  }, [
    isEditing,
    index,
    editedText,
    iconId,
    handleEdit,
  ]);

  const handleIconButtonClick = useCallback(() => {
    if (isEditing) {
      setPickerAnchorEl(iconButtonRef.current);
    }
  }, [isEditing]);

  const handlePickerClose = useCallback(() => {
    setPickerAnchorEl(null);
  }, []);

  const Icon = getIconComponent(iconId);

  return (
    <StyledDraggable>
      <ListItem disabled={isHidden}>
        <ListItemIcon className="drag-handle">
          <Reorder />
        </ListItemIcon>
        <StyledIconButton
          ref={iconButtonRef}
          $isEditing={isEditing}
          onClick={handleIconButtonClick}
          size="small"
          disableRipple={!isEditing}
        >
          <Icon size={20} />
        </StyledIconButton>
        <StyledListItemText
          $isHidden={isHidden}
          $isEditing={isEditing}
          primary={isEditing ? (
            <TextField
              onChange={(e) => setEditedText(e.target.value)}
              value={editedText}
              inputProps={{ maxLength: MAX_HABIT_NAME_LENGTH }}
            />
          ) : habitText}
        />
        <ListItemSecondaryAction>
          <ListItemIcon onClick={() => handleEditClick(index)}>
            {isEditing ? <Done /> : <Edit />}
          </ListItemIcon>
          <ListItemIcon onClick={() => handleHide(index)}>
            {isHidden ? <VisibilityOffOutlined /> : <VisibilityOutlined />}
          </ListItemIcon>
          <ListItemIcon onClick={() => handleDelete(index)}>
            <Delete />
          </ListItemIcon>
        </ListItemSecondaryAction>
      </ListItem>
      <HabitIconPicker
        anchorEl={pickerAnchorEl}
        selectedIconId={iconId}
        usedIconIds={usedIconIds.filter((usedIconId) => usedIconId !== iconId)}
        onChange={onIconChange}
        onClose={handlePickerClose}
      />
    </StyledDraggable>
  );
};

HabitItem.propTypes = {
  index: PropTypes.number.isRequired,
  habitText: PropTypes.string.isRequired,
  handleDelete: PropTypes.func.isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleHide: PropTypes.func.isRequired,
  isHidden: PropTypes.bool.isRequired,
  iconId: PropTypes.string,
  onIconChange: PropTypes.func.isRequired,
  usedIconIds: PropTypes.arrayOf(PropTypes.string),
};

HabitItem.defaultProps = {
  iconId: null,
  usedIconIds: [],
};

export default HabitItem;
